/**
 * Contenido del blog de Nukli — estático y autocontenido (sin backend).
 * Pensado para SEO: cada artículo apunta a una búsqueda frecuente.
 *
 * Para añadir un artículo: copia un objeto, cambia `slug` (único, en-kebab-case),
 * la fecha y el contenido por idioma. Si un idioma falta, se usa `en`.
 */

import { GENERATED_POSTS } from './generated-posts';

const HAND_POSTS = [
  {
    slug: 'como-funciona-nukli-mcp-datos-reales',
    date: '2026-08-03',
    readingTime: 7,
    icon: 'mdi-plug-zap',
    tag: { es: 'Casos de uso', en: 'Use cases' },
    mockupCaption: {
      es: 'Así lo ve el usuario: cada paso consulta un dato real antes de escribir, y el borrador solo se envía cuando tú lo apruebas.',
      en: 'What the user sees: every step checks real data before writing, and the draft only goes out once you approve it.',
    },
    es: {
      title: 'Cómo funciona Nukli por dentro: de la queja de un cliente a una respuesta verificada en 10 segundos',
      description: 'Un caso real: cómo Nukli consulta Stripe y las políticas de tu empresa mediante MCP para redactar una respuesta de atención al cliente con datos comprobados, no inventados.',
      keywords: 'IA atención al cliente, conectar Stripe con el correo, MCP email, responder clientes con IA, datos fiables IA',
      body: `
<p>La mayoría de asistentes de IA para correo tienen el mismo problema: escriben bonito, pero no <em>saben</em> nada. Pueden redactar una disculpa perfecta a un cliente… inventándose el número de pedido, el importe y la política de devoluciones. En atención al cliente, un dato inventado es peor que no responder.</p>

<p>Nukli resuelve esto de otra forma: antes de escribir una sola palabra, <strong>va a buscar los datos reales</strong> a los servicios donde viven. Te lo enseñamos con un caso real.</p>

<h2>El caso: "falta un cuaderno en mi pedido"</h2>
<p>Una tienda online de material de estudio recibe este correo: un cliente ha comprado un pack de seis cuadernos de preparación de exámenes, pero en el paquete falta el dossier de inglés. El encargado abre el hilo en Nukli y pide al asistente: <em>"Redacta la respuesta definitiva a este correo: enviaremos el dossier de inglés que falta"</em>.</p>

<h2>Qué hace Nukli en los siguientes 10 segundos</h2>
<p>En lugar de ponerse a escribir, el asistente encadena una serie de comprobaciones, cada una visible en pantalla:</p>
<ol>
  <li><strong>Busca el pago en Stripe:</strong> localiza la transacción del cliente por su email.</li>
  <li><strong>Consulta el pedido:</strong> confirma el importe abonado (89,70 €) y el detalle línea a línea: los seis cuadernos, incluido el de inglés. El cliente pagó por él; el error fue del envío.</li>
  <li><strong>Revisa el historial del contacto:</strong> ¿es la primera incidencia o un caso recurrente?</li>
  <li><strong>Busca respuestas parecidas:</strong> recupera cómo se resolvieron casos similares para mantener el mismo criterio y el mismo tono.</li>
  <li><strong>Consulta las políticas de la empresa:</strong> ante un error de envío o producto faltante, la empresa asume los costes del reenvío.</li>
  <li><strong>Redacta el borrador:</strong> una respuesta que pide disculpas, confirma con la referencia del pago que el dossier estaba pagado y anuncia el reenvío sin coste, tal y como marca la política.</li>
</ol>
<p>El borrador aparece en la caja de respuesta, listo para revisar y enviar. Total: menos de 10 segundos.</p>
<!--mockup:agent-->

<h2>La clave: MCP, o cómo la IA deja de inventar</h2>
<p>Todo esto es posible gracias a <strong>MCP (Model Context Protocol)</strong>, un estándar abierto que permite a un asistente de IA conectarse a otros servicios de forma segura. En Nukli conectas tus herramientas —Stripe, tu base de datos, tu documentación interna— y el asistente puede consultarlas cuando el contexto lo pide.</p>
<p>La diferencia es radical: el importe del pedido no lo "recuerda" el modelo, lo <strong>lee de Stripe en ese momento</strong>. La política de reenvíos no es una suposición plausible, es <strong>tu política real</strong>, consultada en tu documentación. Si el cliente no hubiera pagado el dossier, el borrador habría sido otro.</p>

<h2>Leer es automático; escribir, solo con tu permiso</h2>
<p>Nukli distingue entre dos tipos de acciones:</p>
<ul>
  <li><strong>Lecturas</strong> (buscar un pago, consultar un pedido, revisar una política): se ejecutan automáticamente. Son inofensivas y son justo lo que aporta fiabilidad.</li>
  <li><strong>Escrituras</strong> (crear un reembolso, modificar un registro, enviar el correo): el asistente las deja preparadas como <em>propuestas</em> que tú confirmas con un clic. Nada sale de tu bandeja ni toca tus sistemas sin tu visto bueno.</li>
</ul>
<p>El resultado es un flujo donde la IA hace el trabajo de investigación —el que de verdad consume tiempo— y tú conservas el control sobre todo lo que tiene consecuencias.</p>

<h2>Por qué esto cambia la atención al cliente</h2>
<p>Responder bien a la incidencia de este caso a mano implica: abrir Stripe, buscar el pago, comprobar el detalle del pedido, recordar (o buscar) la política de reenvíos y redactar el correo. Cinco minutos si tienes práctica, con la pestaña de Stripe, la del correo y la del manual interno abiertas a la vez.</p>
<p>Con Nukli, la misma respuesta —con los mismos datos verificados— está lista antes de que te dé tiempo a abrir la segunda pestaña. Y no es una plantilla: cada comprobación se hace de nuevo para cada correo.</p>

<h2>Pruébalo con tus propias herramientas</h2>
<p>El catálogo de conexiones de Nukli te permite enlazar los servicios que ya usas y añadir cualquier servidor MCP propio. Conecta tu correo, conecta tus datos, y deja que las respuestas salgan con hechos, no con suposiciones.</p>
`,
    },
    en: {
      title: 'Inside Nukli: from a customer complaint to a verified reply in 10 seconds',
      description: 'A real case: how Nukli queries Stripe and your company policies through MCP to draft a customer support reply with verified data — not made-up facts.',
      keywords: 'AI customer support, connect Stripe to email, MCP email, reply to customers with AI, reliable AI data',
      body: `
<p>Most AI email assistants share the same flaw: they write beautifully, but they don't <em>know</em> anything. They can draft a perfect apology to a customer… while making up the order number, the amount and the refund policy. In customer support, an invented fact is worse than no reply at all.</p>

<p>Nukli takes a different approach: before writing a single word, it <strong>goes and fetches the real data</strong> from the services where it lives. Here's a real case.</p>

<h2>The case: "a workbook is missing from my order"</h2>
<p>An online store selling study material receives this email: a customer bought a pack of six exam-prep workbooks, but the English dossier is missing from the package. The manager opens the thread in Nukli and asks the assistant: <em>"Draft the final reply to this email: we'll send the missing English dossier"</em>.</p>

<h2>What Nukli does in the next 10 seconds</h2>
<p>Instead of jumping straight into writing, the assistant chains a series of checks, each visible on screen:</p>
<ol>
  <li><strong>Finds the payment in Stripe:</strong> locates the customer's transaction by their email address.</li>
  <li><strong>Checks the order:</strong> confirms the amount paid (€89.70) and the line-by-line detail: all six workbooks, English included. The customer paid for it; the mistake was in the shipment.</li>
  <li><strong>Reviews the contact's history:</strong> is this a first incident or a recurring case?</li>
  <li><strong>Searches similar replies:</strong> retrieves how comparable cases were resolved, to keep the same criteria and tone.</li>
  <li><strong>Checks company policies:</strong> for shipping errors or missing products, the company covers the reshipment costs.</li>
  <li><strong>Drafts the reply:</strong> a message that apologizes, confirms with the payment reference that the dossier was paid for, and announces a free reshipment, exactly as the policy dictates.</li>
</ol>
<p>The draft lands in the reply box, ready to review and send. Total: under 10 seconds.</p>
<!--mockup:agent-->

<h2>The key: MCP, or how AI stops making things up</h2>
<p>All of this is possible thanks to <strong>MCP (Model Context Protocol)</strong>, an open standard that lets an AI assistant connect to other services securely. In Nukli you connect your tools — Stripe, your database, your internal documentation — and the assistant can query them whenever the context calls for it.</p>
<p>The difference is radical: the order amount isn't something the model "remembers", it <strong>reads it from Stripe at that moment</strong>. The reshipment policy isn't a plausible guess, it's <strong>your actual policy</strong>, looked up in your documentation. If the customer hadn't paid for the dossier, the draft would have been a different one.</p>

<h2>Reading is automatic; writing needs your approval</h2>
<p>Nukli distinguishes two kinds of actions:</p>
<ul>
  <li><strong>Reads</strong> (finding a payment, checking an order, looking up a policy): executed automatically. They're harmless — and they're exactly what makes the reply reliable.</li>
  <li><strong>Writes</strong> (issuing a refund, modifying a record, sending the email): the assistant prepares them as <em>proposals</em> you confirm with one click. Nothing leaves your inbox or touches your systems without your sign-off.</li>
</ul>
<p>The result is a workflow where the AI does the research — the part that actually eats your time — and you keep control over everything with consequences.</p>

<h2>Why this changes customer support</h2>
<p>Handling this case well by hand means: opening Stripe, finding the payment, checking the order detail, remembering (or looking up) the reshipment policy and writing the email. Five minutes if you're practiced, with the Stripe tab, the email tab and the internal handbook all open at once.</p>
<p>With Nukli, the same reply — with the same verified data — is ready before you've opened the second tab. And it's not a template: every check runs fresh for every email.</p>

<h2>Try it with your own tools</h2>
<p>Nukli's connection catalog lets you link the services you already use and add any MCP server of your own. Connect your email, connect your data, and let your replies go out with facts, not guesses.</p>
`,
    },
  },
  {
    slug: 'automatizar-respuestas-correo-ia',
    date: '2026-05-12',
    readingTime: 6,
    icon: 'mdi-robot-happy-outline',
    tag: { es: 'Automatización', en: 'Automation' },
    es: {
      title: 'Cómo automatizar las respuestas de tu correo con IA (sin sonar a robot)',
      description: 'Guía práctica para responder correos en segundos con inteligencia artificial, mantener tu tono y no perder horas en la bandeja de entrada.',
      keywords: 'automatizar correo, respuestas automáticas, IA para email, responder emails rápido',
      body: `
<p>La mayoría de las personas pasan más de <strong>dos horas al día</strong> gestionando el correo. La buena noticia: gran parte de ese tiempo se puede recuperar. No hablamos de respuestas automáticas frías y genéricas, sino de una IA que lee el contexto y escribe como tú.</p>

<h2>Por qué las respuestas automáticas tradicionales no funcionan</h2>
<p>Las plantillas fijas y los "fuera de la oficina" resuelven casos muy concretos, pero fallan en lo importante: cada correo es distinto. Una buena respuesta necesita entender el hilo, el tono y la intención. Por eso copiar y pegar plantillas acaba sonando impersonal.</p>

<h2>Los 3 niveles de automatización del correo</h2>
<ul>
  <li><strong>Nivel 1 — Plantillas:</strong> útiles para mensajes repetitivos, pero rígidas.</li>
  <li><strong>Nivel 2 — Reglas:</strong> clasifican, etiquetan y archivan solas. Ahorran orden, no escritura.</li>
  <li><strong>Nivel 3 — IA contextual:</strong> lee la conversación completa y redacta un borrador listo para revisar y enviar.</li>
</ul>

<h2>Cómo automatizar tus respuestas paso a paso</h2>
<h3>1. Conecta tu correo</h3>
<p>Funciona con Gmail, Outlook o cualquier cuenta IMAP. Todo tu correo en un mismo sitio, sin cambiar de proveedor.</p>
<h3>2. Deja que la IA proponga el borrador</h3>
<p>En lugar de mirar la pantalla en blanco, recibes una respuesta contextual en tu idioma y tu tono. La IA ya ha leído el hilo por ti.</p>
<h3>3. Revisa, ajusta y envía</h3>
<p>Cambia una palabra si quieres, acorta el mensaje o cambia el tono con un clic. Tú mantienes el control; la IA hace el trabajo pesado.</p>

<h2>Consejos para que no suene a robot</h2>
<ul>
  <li>Define tu tono (cercano, formal, directo) y reutilízalo.</li>
  <li>Crea asistentes distintos para cada rol o empresa que gestionas.</li>
  <li>Revisa siempre antes de enviar: la IA acierta el 90%, tú pones el 10% final.</li>
</ul>

<p>Automatizar el correo no significa despersonalizarlo. Significa dejar de escribir desde cero cada mensaje y dedicar tu tiempo a lo que de verdad importa.</p>
`,
    },
    en: {
      title: 'How to automate your email replies with AI (without sounding like a robot)',
      description: 'A practical guide to replying to emails in seconds with AI, keeping your tone and saving hours in your inbox.',
      keywords: 'automate email, automatic replies, AI for email, reply to emails fast',
      body: `
<p>Most people spend more than <strong>two hours a day</strong> on email. The good news: most of that time is recoverable. We are not talking about cold, generic auto-replies, but about AI that reads context and writes like you do.</p>

<h2>Why traditional auto-replies don't work</h2>
<p>Fixed templates and "out of office" messages solve narrow cases, but they fail where it matters: every email is different. A good reply needs to understand the thread, the tone and the intent. That is why copy-pasting templates ends up sounding impersonal.</p>

<h2>The 3 levels of email automation</h2>
<ul>
  <li><strong>Level 1 — Templates:</strong> useful for repetitive messages, but rigid.</li>
  <li><strong>Level 2 — Rules:</strong> sort, label and archive on their own. They save order, not writing.</li>
  <li><strong>Level 3 — Contextual AI:</strong> reads the whole conversation and drafts a reply ready to review and send.</li>
</ul>

<h2>How to automate your replies step by step</h2>
<h3>1. Connect your email</h3>
<p>Works with Gmail, Outlook or any IMAP account. All your mail in one place, no need to switch providers.</p>
<h3>2. Let AI draft the reply</h3>
<p>Instead of a blank screen, you get a contextual reply in your language and tone. The AI has already read the thread for you.</p>
<h3>3. Review, tweak and send</h3>
<p>Change a word if you want, shorten the message or switch tone with a click. You stay in control; the AI does the heavy lifting.</p>

<h2>Tips so it never sounds robotic</h2>
<ul>
  <li>Define your tone (friendly, formal, direct) and reuse it.</li>
  <li>Create different assistants for each role or company you manage.</li>
  <li>Always review before sending: AI gets 90% right, you add the final 10%.</li>
</ul>

<p>Automating email doesn't mean depersonalizing it. It means you stop writing every message from scratch and spend your time on what truly matters.</p>
`,
    },
  },
  {
    slug: 'escribir-correos-profesionales-rapido',
    date: '2026-05-20',
    readingTime: 5,
    icon: 'mdi-feather',
    tag: { es: 'Productividad', en: 'Productivity' },
    es: {
      title: 'Cómo escribir correos profesionales más rápido (y mejor)',
      description: 'Estructura, fórmulas y atajos para redactar emails profesionales claros en la mitad de tiempo, con ejemplos listos para usar.',
      keywords: 'escribir correos profesionales, redactar emails, plantillas de correo, email profesional',
      body: `
<p>Un buen correo profesional no es el más largo, es el más claro. Estas son las claves para escribir más rápido sin perder calidad.</p>

<h2>La estructura que siempre funciona</h2>
<ol>
  <li><strong>Asunto concreto:</strong> di qué quieres en 5–7 palabras.</li>
  <li><strong>Primera línea con el objetivo:</strong> no hagas esperar al lector.</li>
  <li><strong>Cuerpo en frases cortas:</strong> una idea por párrafo.</li>
  <li><strong>Llamada a la acción clara:</strong> qué esperas y para cuándo.</li>
</ol>

<h2>Fórmulas rápidas según el caso</h2>
<ul>
  <li><strong>Pedir algo:</strong> contexto breve → petición concreta → fecha.</li>
  <li><strong>Decir que no:</strong> agradece → declina con una razón → ofrece alternativa.</li>
  <li><strong>Hacer seguimiento:</strong> recuerda el hilo → pregunta puntual → facilita la respuesta.</li>
</ul>

<h2>Los errores que te hacen lento</h2>
<ul>
  <li>Escribir desde cero cada vez en lugar de partir de un borrador.</li>
  <li>Reescribir el mismo mensaje en otro idioma a mano.</li>
  <li>Corregir la ortografía al final, en vez de hacerlo mientras escribes.</li>
</ul>

<h2>Cómo la IA acelera el proceso</h2>
<p>Con un asistente de IA en tu cliente de correo, partes de un borrador contextual en lugar de una pantalla en blanco. Traduces y corriges antes de enviar, y mantienes un tono coherente en todos tus mensajes. El resultado: correos mejores en mucho menos tiempo.</p>
`,
    },
    en: {
      title: 'How to write professional emails faster (and better)',
      description: 'Structure, formulas and shortcuts to write clear professional emails in half the time, with ready-to-use examples.',
      keywords: 'write professional emails, email writing, email templates, professional email',
      body: `
<p>A good professional email isn't the longest one, it's the clearest. Here are the keys to writing faster without losing quality.</p>

<h2>The structure that always works</h2>
<ol>
  <li><strong>Specific subject line:</strong> say what you want in 5–7 words.</li>
  <li><strong>Open with the goal:</strong> don't make the reader wait.</li>
  <li><strong>Short sentences in the body:</strong> one idea per paragraph.</li>
  <li><strong>Clear call to action:</strong> what you expect and by when.</li>
</ol>

<h2>Quick formulas by scenario</h2>
<ul>
  <li><strong>Asking for something:</strong> brief context → specific request → deadline.</li>
  <li><strong>Saying no:</strong> thank → decline with a reason → offer an alternative.</li>
  <li><strong>Following up:</strong> recall the thread → one precise question → make replying easy.</li>
</ul>

<h2>The mistakes that slow you down</h2>
<ul>
  <li>Writing from scratch every time instead of starting from a draft.</li>
  <li>Rewriting the same message in another language by hand.</li>
  <li>Spell-checking at the end instead of as you write.</li>
</ul>

<h2>How AI speeds it up</h2>
<p>With an AI assistant inside your mail client, you start from a contextual draft instead of a blank screen. You translate and proofread before sending, and keep a consistent tone across messages. The result: better emails in much less time.</p>
`,
    },
  },
  {
    slug: 'gestionar-correo-multilingue',
    date: '2026-05-28',
    readingTime: 5,
    icon: 'mdi-translate',
    tag: { es: 'Idiomas', en: 'Languages' },
    es: {
      title: 'Cómo gestionar el correo en varios idiomas sin perder tiempo',
      description: 'Trucos para responder emails en cualquier idioma con el tono correcto, traducir sin copiar y pegar y evitar errores con clientes internacionales.',
      keywords: 'correo multilingüe, traducir emails, responder en otros idiomas, email internacional',
      body: `
<p>Si trabajas con clientes de varios países, el idioma puede convertirse en un cuello de botella. Cada mensaje implica traducir, revisar y rezar para no haber cometido un error. Hay una forma mejor.</p>

<h2>El problema de traducir a mano</h2>
<p>Copiar el texto a un traductor, pegarlo de vuelta y ajustar el tono rompe tu flujo y multiplica los errores. Además, una traducción literal rara vez suena natural.</p>

<h2>3 claves para un correo multilingüe impecable</h2>
<ul>
  <li><strong>Escribe en tu idioma, envía en el del cliente:</strong> redacta cómodo y deja que la IA traduzca con el tono adecuado.</li>
  <li><strong>Detecta el idioma automáticamente:</strong> responde en el mismo idioma del mensaje recibido, sin pensarlo.</li>
  <li><strong>Revisa gramática y estilo:</strong> antes de enviar, no después de meter la pata.</li>
</ul>

<h2>Mantén el tono, no solo las palabras</h2>
<p>Un buen asistente no solo traduce: adapta la formalidad. No es lo mismo escribir a un cliente japonés que a uno alemán o estadounidense. La IA contextual ajusta el registro por ti.</p>

<h2>Cómo lo resuelve Nukli</h2>
<p>Nukli detecta el idioma del hilo, redacta la respuesta en ese idioma y corrige antes de enviar. Más de 40 idiomas, sin salir de tu bandeja de entrada y sin copiar y pegar.</p>
`,
    },
    en: {
      title: 'How to handle multilingual email without wasting time',
      description: 'Tips to reply to emails in any language with the right tone, translate without copy-pasting and avoid mistakes with international clients.',
      keywords: 'multilingual email, translate emails, reply in other languages, international email',
      body: `
<p>If you work with clients across countries, language can become a bottleneck. Every message means translating, reviewing and hoping you didn't make a mistake. There's a better way.</p>

<h2>The problem with translating by hand</h2>
<p>Copying text into a translator, pasting it back and fixing the tone breaks your flow and multiplies errors. And a literal translation rarely sounds natural.</p>

<h2>3 keys to flawless multilingual email</h2>
<ul>
  <li><strong>Write in your language, send in theirs:</strong> draft comfortably and let AI translate with the right tone.</li>
  <li><strong>Detect the language automatically:</strong> reply in the same language as the message you received, without thinking.</li>
  <li><strong>Check grammar and style:</strong> before sending, not after slipping up.</li>
</ul>

<h2>Keep the tone, not just the words</h2>
<p>A good assistant doesn't just translate: it adapts formality. Writing to a Japanese client isn't the same as writing to a German or American one. Contextual AI adjusts the register for you.</p>

<h2>How Nukli solves it</h2>
<p>Nukli detects the language of the thread, drafts the reply in that language and proofreads before sending. Over 40 languages, without leaving your inbox and without copy-pasting.</p>
`,
    },
  },
  {
    slug: 'mejor-cliente-correo-mac-ia',
    date: '2026-06-04',
    readingTime: 7,
    icon: 'mdi-apple',
    tag: { es: 'Guías', en: 'Guides' },
    es: {
      title: 'El mejor cliente de correo con IA para Mac en 2026: qué buscar',
      description: 'Comparativa de lo que importa al elegir un cliente de correo con IA para Mac: privacidad, modelos, idiomas, precio y experiencia de uso.',
      keywords: 'mejor cliente de correo mac, cliente email con IA, app de correo macOS, alternativa a Mail',
      body: `
<p>Elegir cliente de correo en Mac ya no va solo de buzones y carpetas. La diferencia real en 2026 es cuánto te ayuda la IA a vaciar la bandeja. Esto es lo que conviene mirar.</p>

<h2>1. ¿La IA entiende el contexto?</h2>
<p>Lo importante no es que "tenga IA", sino que lea el hilo completo y redacte respuestas que de verdad puedas enviar. Desconfía de las funciones que solo generan texto genérico.</p>

<h2>2. Privacidad y seguridad</h2>
<p>Tu correo es sensible. Busca credenciales cifradas, una política clara de datos y la garantía de que nadie vende tu información.</p>

<h2>3. Compatibilidad real</h2>
<ul>
  <li>Gmail y Microsoft 365 con inicio de sesión seguro (OAuth).</li>
  <li>Soporte de cualquier cuenta IMAP.</li>
  <li>Varias cuentas a la vez.</li>
</ul>

<h2>4. Idiomas</h2>
<p>Si trabajas con clientes internacionales, la traducción y la corrección integradas valen oro. Comprueba cuántos idiomas soporta y si mantiene el tono.</p>

<h2>5. Una app rápida y cuidada</h2>
<p>Atajos de teclado y modo oscuro, disponible en Mac, Windows y Linux. Las webs lentas dentro de una ventana no cuentan.</p>

<h2>6. Precio que crece contigo</h2>
<p>Lo ideal: empezar gratis y pagar solo cuando necesitas más IA. Sin permanencia y con la opción de cancelar cuando quieras.</p>

<h2>Dónde encaja Nukli</h2>
<p>Nukli es un cliente de correo con IA disponible para Mac, Windows y Linux, con un asistente que lee tus hilos, redacta respuestas en tu tono, traduce a más de 40 idiomas y corrige antes de enviar. Empieza gratis y sube de plan cuando lo necesites.</p>
`,
    },
    en: {
      title: 'The best AI email client for Mac in 2026: what to look for',
      description: 'What actually matters when picking an AI email client for Mac: privacy, models, languages, price and the overall experience.',
      keywords: 'best mac email client, AI email app, macOS mail app, Mail alternative',
      body: `
<p>Choosing a mail client on Mac is no longer just about mailboxes and folders. The real difference in 2026 is how much the AI helps you clear your inbox. Here's what to look for.</p>

<h2>1. Does the AI understand context?</h2>
<p>What matters isn't that it "has AI", but that it reads the full thread and drafts replies you can actually send. Be wary of features that only generate generic text.</p>

<h2>2. Privacy and security</h2>
<p>Your email is sensitive. Look for encrypted credentials, a clear data policy and a guarantee that nobody sells your information.</p>

<h2>3. Real compatibility</h2>
<ul>
  <li>Gmail and Microsoft 365 with secure sign-in (OAuth).</li>
  <li>Support for any IMAP account.</li>
  <li>Multiple accounts at once.</li>
</ul>

<h2>4. Languages</h2>
<p>If you work with international clients, built-in translation and proofreading are worth gold. Check how many languages it supports and whether it keeps the tone.</p>

<h2>5. A fast, polished app</h2>
<p>Keyboard shortcuts and dark mode, available on Mac, Windows and Linux. Slow web pages inside a window don't count.</p>

<h2>6. Pricing that grows with you</h2>
<p>The ideal: start free and pay only when you need more AI. No lock-in and the option to cancel anytime.</p>

<h2>Where Nukli fits</h2>
<p>Nukli is an AI email client available on Mac, Windows and Linux, with an assistant that reads your threads, drafts replies in your tone, translates to 40+ languages and proofreads before sending. Start free and upgrade when you need to.</p>
`,
    },
  },
];

// Engine-generated posts first, then the hand-written ones; newest by date.
export const POSTS = [...GENERATED_POSTS, ...HAND_POSTS]
  .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));

export function getPost(slug) {
  return POSTS.find((p) => p.slug === slug) || null;
}

export function localized(post, locale) {
  if (!post) return null;
  const l = post[locale] || post.en;
  return {
    slug: post.slug,
    date: post.date,
    readingTime: post.readingTime,
    icon: post.icon,
    tag: (post.tag && (post.tag[locale] || post.tag.en)) || '',
    mockupCaption: (post.mockupCaption && (post.mockupCaption[locale] || post.mockupCaption.en)) || '',
    ...l,
  };
}
